import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  InputAudioBufferSpeechEvent,
  InputAudioTranscriptionCompleted,
  RealTimeClientEvent,
  ResponseDelta,
  ResponseDone,
  ResponseTextAudioTranscriptDelta,
  ResponseTextDelta,
  ResponseTextDone,
} from 'views/EMR/components/AIAssistant/types';
import useGetRemoteSdp from 'views/EMR/components/AIAssistant/hooks';
import {
  uuid,
} from 'utils/misc';

type UseRealTimeAIAgentProps = {
  onReceivedResponseDone?: (data: ResponseDone) => void;
  onReceivedResponseTextDone?: (data: ResponseTextDone) => void;
  onReceivedResponseTextDelta?: (data: ResponseTextDelta) => void;
  onReceivedResponseAudioTranscriptDelta?: (data: ResponseTextAudioTranscriptDelta) => void;
  onReceivedInputAudioBufferSpeechStarted?: (data: InputAudioBufferSpeechEvent) => void;
  onReceivedInputAudioBufferSpeechStopped?: (data: InputAudioBufferSpeechEvent) => void;
  onReceivedInputAudioTranscriptionCompleted?: (data: InputAudioTranscriptionCompleted) => void;
  onReceivedError?: (data: ErrorEvent) => void;
  onDefaultMessage?: (data: any) => void;
  onSessionStarted?: () => void;
};

export default function useRealTimeAIAgent({
  onReceivedResponseDone,
  onReceivedResponseTextDone,
  onReceivedResponseTextDelta,
  onReceivedResponseAudioTranscriptDelta,
  onReceivedInputAudioBufferSpeechStarted,
  onReceivedInputAudioTranscriptionCompleted,
  onReceivedInputAudioBufferSpeechStopped,
  onReceivedError,
  onDefaultMessage,
  onSessionStarted,
}: UseRealTimeAIAgentProps) {
  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
  const [localMediaStream, setLocalMediaStream] = useState<MediaStream | null>(null);
  const [responseDelta, setResponseDelta] = useState<ResponseDelta>({
    id: '',
    text: '',
  });

  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const audioElement = useRef<HTMLAudioElement | null>(null);

  const { mutateAsync: getRemoteSdp } = useGetRemoteSdp();

  const startSession = async (EPHEMERAL_KEY: string) => {
    // Create a peer connection
    const pc = new RTCPeerConnection();

    // Set up to play remote audio from the model
    audioElement.current = document.createElement('audio');
    // audioElement.current!.autoplay = true;
    pc.ontrack = ({ streams: [stream] }) => {
      audioElement.current!.srcObject = stream;
    };

    // Add local audio track for microphone input in the browser
    const ms = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    pc.addTrack(ms.getTracks()[0]);
    setLocalMediaStream(ms);
    // Set up data channel for sending and receiving events
    const dc = pc.createDataChannel('oai-events');
    setDataChannel(dc);

    // Start the session using the Session Description Protocol (SDP)
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const remoteSdp = await getRemoteSdp({
      localSdp: offer.sdp ?? '',
      realTimeToken: EPHEMERAL_KEY,
    });

    const answer = {
      type: 'answer',
      sdp: remoteSdp,
    } as RTCSessionDescriptionInit;
    await pc.setRemoteDescription(answer);

    peerConnection.current = pc;
  };

  const stopSession = () => {
    if (!isNil(dataChannel)) {
      dataChannel.close();
    }

    if (!isNil(peerConnection.current)) {
      peerConnection.current.close();
    }

    localMediaStream?.getTracks().forEach((track) => {
      track.stop();
    });

    setDataChannel(null);
    peerConnection.current = null;
  };

  const sendClientEvent = (event: RealTimeClientEvent) => {
    if (!isNil(dataChannel)) {
      event.event_id = event?.event_id || `client_event_${uuid()}`;
      dataChannel.send(JSON.stringify(event));
    } else {
      console.error(
        'Failed to send message - no data channel available',
        event,
      );
    }
  };

  const addContext = (context: string) => {
    const eventId = `user_${uuid()}`;
    const event: RealTimeClientEvent = {
      event_id: eventId,
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: context,
          },
        ],
      },
    };
    sendClientEvent(event);

    sendClientEvent({
      type: 'response.create',
      response: { modalities: ['text'] },
    });
  };

  const sendUserTextMessage = (message: string) => {
    const eventId = `user_${uuid()}`;
    const event: RealTimeClientEvent = {
      event_id: eventId,
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: message,
          },
        ],
      },
    };
    sendClientEvent(event);
    return eventId;
  };

  const onMessage = (e: MessageEvent<any>) => {
    try {
      const data = JSON.parse(e.data);
      switch (data.type) {
        case 'response.created':
          setResponseDelta({ id: data.response.id, text: '' });
          break;
        case 'response.done':
          setResponseDelta({ id: '', text: '' });
          onReceivedResponseDone?.(data);
          break;
        case 'response.text.done':
          setResponseDelta({ id: '', text: '' });
          onReceivedResponseTextDone?.(data);
          break;
        case 'response.text.delta':
          setResponseDelta((prev) => ({ ...prev, text: prev.text + data.delta }));
          onReceivedResponseTextDelta?.(data);
          break;
        case 'response.audio_transcript.delta':
          setResponseDelta((prev) => ({ ...prev, text: prev.text + data.delta }));
          onReceivedResponseAudioTranscriptDelta?.(data);
          break;
        case 'input_audio_buffer.speech_started':
          onReceivedInputAudioBufferSpeechStarted?.(data);
          break;
        case 'input_audio_buffer.speech_stopped':
          onReceivedInputAudioBufferSpeechStopped?.(data);
          break;
        case 'conversation.item.input_audio_transcription.completed':
          onReceivedInputAudioTranscriptionCompleted?.(data);
          break;
        case 'error':
          onReceivedError?.(data);
          break;
        default:
          onDefaultMessage?.(data);
      }
    } catch (error) {
      console.error('Error parsing message:', e, error);
    }
  };

  useEffect(() => {
    if (!isNil(dataChannel)) {
      // Append new server events to the list
      dataChannel.addEventListener('message', onMessage);

      // Set session active when the data channel is opened
      dataChannel.addEventListener('open', onSessionStarted ?? noop);
    }

    return () => {
      dataChannel?.removeEventListener('message', onMessage);
      dataChannel?.removeEventListener('open', onSessionStarted ?? noop);
    };
  }, [dataChannel]);

  return {
    startSession,
    stopSession,
    sendUserTextMessage,
    addContext,
    responseDelta,
  };
}
